import { Upload, GoogleDriveLogo, ArrowsClockwise, FileArchive } from "@phosphor-icons/react";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/** Formulário de origem do restore: tabs upload/drive + input do arquivo/lista */
export default function RestoreSourceForm({
  mode,
  driveConfigured,
  restoring,
  file,
  setFile,
  driveFiles,
  driveFilesLoading,
  selectedDriveFile,
  setSelectedDriveFile,
  switchMode,
}) {
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-black">
        <button
          className={`px-4 py-2 text-sm font-bold border-b-2 ${mode === "upload" ? "border-black text-black" : "border-transparent text-zinc-500"}`}
          onClick={() => switchMode("upload")}
          disabled={restoring}
          data-testid="restore-tab-upload"
        >
          <Upload size={14} weight="bold" className="inline mr-1" /> Enviar do computador
        </button>
        <button
          className={`px-4 py-2 text-sm font-bold border-b-2 ${mode === "drive" ? "border-black text-black" : "border-transparent text-zinc-500"}`}
          onClick={() => switchMode("drive")}
          disabled={restoring || !driveConfigured}
          title={driveConfigured ? "" : "Google Drive ainda não configurado"}
          data-testid="restore-tab-drive"
        >
          <GoogleDriveLogo size={14} weight="bold" className="inline mr-1" /> Do Google Drive
        </button>
      </div>

      {mode === "upload" && (
        <UploadPanel file={file} setFile={setFile} restoring={restoring} />
      )}
      {mode === "drive" && (
        <DrivePanel
          restoring={restoring}
          driveFiles={driveFiles}
          driveFilesLoading={driveFilesLoading}
          selectedDriveFile={selectedDriveFile}
          setSelectedDriveFile={setSelectedDriveFile}
          onRefresh={() => switchMode("drive")}
        />
      )}
    </div>
  );
}

function UploadPanel({ file, setFile, restoring }) {
  return (
    <div className="space-y-3" data-testid="restore-upload-panel">
      <label className="label">Arquivo .archive.gz</label>
      <input
        type="file"
        accept=".gz,.archive"
        className="input"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        disabled={restoring}
        data-testid="restore-file-input"
      />
      {file && (
        <div className="text-xs text-zinc-600 flex items-center gap-2 border border-zinc-300 p-2">
          <FileArchive size={16} />
          <div>
            <div className="font-mono">{file.name}</div>
            <div className="text-[10px] text-zinc-500">{fmtSize(file.size)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function DrivePanel({ restoring, driveFiles, driveFilesLoading, selectedDriveFile, setSelectedDriveFile, onRefresh }) {
  const renderList = () => {
    if (driveFilesLoading) {
      return <div className="text-center text-zinc-400 py-8 text-sm">Carregando arquivos...</div>;
    }
    if (driveFiles.length === 0) {
      return (
        <div className="text-center text-zinc-400 py-8 text-sm border border-dashed border-zinc-300">
          Nenhum arquivo .archive.gz encontrado na pasta do Drive.
        </div>
      );
    }
    return (
      <div className="border border-black max-h-64 overflow-y-auto">
        {driveFiles.map((f) => (
          <label
            key={f.id}
            className={`flex items-start gap-3 p-3 border-b border-zinc-200 last:border-b-0 cursor-pointer hover:bg-zinc-100 ${selectedDriveFile?.id === f.id ? "bg-zinc-900 text-white hover:bg-zinc-900" : ""}`}
            data-testid={`restore-drive-file-${f.id}`}
          >
            <input
              type="radio"
              name="drive-file"
              checked={selectedDriveFile?.id === f.id}
              onChange={() => setSelectedDriveFile(f)}
              className="mt-1"
              disabled={restoring}
            />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-xs truncate">{f.name}</div>
              <div className="text-[10px] opacity-70">
                {fmtSize(f.size)} · {fmtDateTime(f.created_time)}
              </div>
            </div>
          </label>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="restore-drive-panel">
      <div className="flex items-center justify-between">
        <label className="label !mb-0">Arquivos disponíveis na pasta do Drive</label>
        <button
          className="btn btn-ghost !py-1 !px-2 text-xs"
          onClick={onRefresh}
          disabled={driveFilesLoading || restoring}
        >
          <ArrowsClockwise size={12} weight="bold" /> Atualizar lista
        </button>
      </div>
      {renderList()}
    </div>
  );
}
